import { useMemo } from "react"
import { Box, Typography, useTheme } from "@mui/material"

import { getIcon } from "utils/icon"

import { useTranslation } from "providers/i18n"
import { useService } from "hooks/service/useService"
import { useAppUI } from "providers/ui"

import { Head } from "components/Head"
import { EmptyData } from "components/EmptyData"
import { MotionDiv, showContent } from "components/motions"

import { ServiceIcon } from "components/icons"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { SearchToggleButton } from "components/domains/search"
import { LanguageButton } from "extensions/iconsiam/components/language/LanguageButton"
import { ServiceCard } from "extensions/iconsiam/components/domains/service/card/index.kiosk-v"
import { VenueSelector } from "components/VenueSelector"
import { useVenue } from "providers/venue"
import { useServiceLocation } from "extensions/iconsiam/hooks/service"
import { IframeModal } from "extensions/iconsiam/components/modal"

export const ServicesKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { t } = useTranslation({ defaultValue: null })
  const { openSearch } = useAppUI()
  const {
    evenColServices,
    oddColServices,
    hasData,
    locationVenueId,
    venueFilter,
    changeVenueFilter,
  } = useService()
  const { externalUrl, handleClickLocation, handleCloseModal } =
    useServiceLocation()
  const isTransportationIframe =
    externalUrl && externalUrl.includes("transportation-dashboard")
  const { venues, isMultiVenueProject } = useVenue()
  const theme = useTheme()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.service) || <ServiceIcon />,
    [theme]
  )

  const { styleOverrides = {} } = theme.components?.[`ServicePage`] || {}
  const {
    root,
    gridContainer,
    gridItem,
    emptyDataContainer,
    navigationWrapper,
    titleTextContainer,
    titleText,
    toolbarContainer,
    actionContainer,
    actionButton,
    searchBarContainer,
    venueSelector,
  } = styleOverrides

  return (
    <>
      <Head title={t("Service & Privilege")} />
      <Layout>
        <Box sx={root}>
          <Box sx={titleTextContainer}>
            <Typography variant="h2" sx={titleText}>
              {t("Service & Privilege Title Page")}
            </Typography>
          </Box>
          {hasData ? (
            <MotionDiv
              variants={showContent}
              initial="initial"
              animate="animate"
              sx={gridContainer}
            >
              <Box sx={gridItem}>
                {evenColServices.map((service, index) => (
                  <ServiceCard
                    onClickLocation={handleClickLocation}
                    key={`service-even-item-${index}`}
                    data={service}
                  />
                ))}
              </Box>
              <Box sx={gridItem}>
                {oddColServices.map((service, index) => {
                  return (
                    <ServiceCard
                      onClickLocation={handleClickLocation}
                      key={`service-odd-item-${index}`}
                      data={service}
                    />
                  )
                })}
              </Box>
            </MotionDiv>
          ) : (
            <MotionDiv
              variants={showContent}
              initial="initial"
              animate="animate"
              sx={emptyDataContainer}
            >
              <EmptyData
                icon={emptyIcon}
                primary={t("No Services")}
                secondary={t("Check Back Soon")}
              />
            </MotionDiv>
          )}
          <Box key="app-navigation" sx={navigationWrapper}>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            <Box sx={toolbarContainer}>
              <Box sx={actionContainer}>
                <LanguageButton sx={actionButton} />
                {additionalMenu}
              </Box>
              <Box sx={searchBarContainer}>
                <SearchToggleButton
                  placeholder={t("Search shops and more...")}
                  onClick={openSearch}
                />
              </Box>
            </Box>
            {AppNavigation && <AppNavigation />}
          </Box>
        </Box>
      </Layout>
      <IframeModal
        src={externalUrl}
        open={!!externalUrl}
        className={
          isTransportationIframe ? "IframeModal-transportation-iframe" : ""
        }
        onClose={handleCloseModal}
      />
    </>
  )
}
